import React, {useEffect, useState} from "react";
import CONSTANTS from "src/utils/constants";
import {Link, StatusIndicator} from "@amzn/awsui-components-react";
import {
    MultidimensionalAllocationDataSourceContext
} from "src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesContext";
import {MultiAllocationDataSourceRecord} from "src/models/multidimensional-allocation/MultiAllocationDataSourceRecord";
import {TPEReactTable} from "src/components/shared/TPEReactTable";
import {
    ErrorPopover, RowContextMenu
} from "src/components/multidimensional-allocation/shared/MultidimensionalAllocationGridCommon";
import {
    MULTI_ALLOCATION_DATA_SOURCE_ACTIONS
} from "src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesReducer";

/**
 * Split grid component displaying data sources below the currently edited record.
 * Shows remaining data sources in a table format with balance pull status and actions.
 */
export default function MultidimensionalAllocationDataSourceSplitGrid() {
    const {services, multidimensionalAllocationDataSourcesState, multidimensionalAllocationDataSourceDispatch, multidimensionalAllocationState} =
        React.useContext(MultidimensionalAllocationDataSourceContext);
    const {datasetRecords, recordBeingEdited} = multidimensionalAllocationDataSourcesState;
    const [formattedRecords, setFormattedRecords] = useState([] as MultiAllocationDataSourceRecord[])
    const { viewMode, worksheetExecution } = multidimensionalAllocationState;
    
    // Check if viewing historical data
    const isViewingHistoricalData = worksheetExecution != null;

    useEffect(() => {
        if (datasetRecords == null) {
            return;
        }
        let records = recordBeingEdited == undefined ? [] : datasetRecords.slice(datasetRecords.indexOf(recordBeingEdited) + 1);
        const startIndex = recordBeingEdited == undefined ? 0 : datasetRecords.indexOf(recordBeingEdited) + 2;
        setFormattedRecords(records.map((x, index) => {
            return {
                ...x,
                sequence: startIndex + index,
                fxRateFormatted: x.fxRate == null ? '-' : services.formattingService.formatString(x.fxRate, true), // Always show decimals for FX rate
                valueFormatted: x.balancePullStatus || 'Not Started',
                enableDataKeyView: recordBeingEdited == null || (recordBeingEdited != null && !recordBeingEdited.isEditing),
            };
        }));
    }, [datasetRecords]);

    const toggleDataKeyView = (recordID: string) => {
        multidimensionalAllocationDataSourceDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.TOGGLE_DATA_KEY_VIEW.withPayload({recordID: recordID}));
    }

    const columnDefinitions = React.useMemo(() => [
        {
            accessor: "sequence",
            Header: "#",
            Cell: (e: any) => <div className="cell-text sequenceCell">{e.value}</div>
        },
        {
            accessor: CONSTANTS.DATA_SOURCE_FIELDS.DATA_SOURCE.ACCESSOR,
            Header: "Data source",
            Cell: (e: any) => <div className={"cell-text dataSourceCell"}>{e.value}</div>
        },
        {
            accessor: CONSTANTS.DATA_SOURCE_FIELDS.PERIOD.ACCESSOR,
            Header: "Period",
            Cell: (e: any) => <div
                className={"cell-text periodCell"}>{e.row.original.customPeriod != null ? e.row.original.customPeriod : e.value}</div>
        },
        {
            accessor: CONSTANTS.DATA_SOURCE_FIELDS.FX_DATE.ACCESSOR,
            Header: "FX date",
            Cell: (e: any) => <div
                className={"cell-text fxDateCell"}>{e.value == null ? null : e.value.split('-').join('/')}</div>
        },
        {
            accessor: CONSTANTS.DATA_SOURCE_FIELDS.FX_TYPE.ACCESSOR,
            Header: "FX type",
            Cell: (e: any) => <div className={"cell-text fxTypeCell"}>{e.value}</div>
        },
        {
            accessor: `${CONSTANTS.DATA_SOURCE_FIELDS.FX_RATE.ACCESSOR}Formatted`,
            Header: "FX rate",
            Cell: (e: any) => <div className={"cell-text fxRateCell"}>{e.value}</div>
        },
        {
            accessor: CONSTANTS.DATA_SOURCE_FIELDS.CURRENCY.ACCESSOR,
            Header: "Currency",
            Cell: (e: any) => <div className={"cell-text currencyCell"}>{e.value}</div>
        },
        {
            accessor: CONSTANTS.DATA_SOURCE_FIELDS.DATA_KEY.ACCESSOR,
            Header: "Data keys",
            Cell: (e: any) => e.row.original.enableDataKeyView ?
                <Link className={"cell-text dataKeyViewLink" + e.row.original.datasourceId}
                      onFollow={() => toggleDataKeyView(e.row.original.datasourceId)}>
                    <div
                        className={"cell-text dataKeyCell"}>{e.row.original[CONSTANTS.DATA_SOURCE_FIELDS.DATA_KEY.ACCESSOR]}</div>
                </Link>
                :
                <div className={"cell-text dataKeyCell"}>{e.value}</div>
        },
        {
            accessor: CONSTANTS.DATA_SOURCE_FIELDS.DESCRIPTION.ACCESSOR,
            Header: "Description",
            Cell: (e: any) => <div className={"cell-text descriptionCell"}>{e.value}</div>
        },
        {
            accessor: `${CONSTANTS.DATA_SOURCE_FIELDS.VALUE.ACCESSOR}Formatted`,
            Header: "Value",
            Cell: (e: any) => <div className={"cell-text valueCell"}>{e.value}</div>
        },
        {
            accessor: "actions",
            Header: "Actions",
            Cell: (e: any) => <div className="rowActions">
                {
                    e.row.original.pullBalanceError != null || (e.row.original.errors != null && (e.row.original.errors as Map<string, string>).size > 0) ?
                        <ErrorPopover error={e.row.original.pullBalanceError || e.row.original.errors}/>
                        :
                        (e.row.original.showSavedIndicator ?
                                <StatusIndicator data-class="smallStatusIndicator"
                                                 type="success">Saved</StatusIndicator>
                                :
                                <span>&nbsp;</span>
                        )
                }
                {
                    <div className='contextMenuPosition'><RowContextMenu row={e.row.original} allowEditActions={true}
                                                                         dispatch={multidimensionalAllocationDataSourceDispatch}
                                                                         recordBeingEdited={recordBeingEdited}
                                                                         isViewingHistoricalData={isViewingHistoricalData}/></div>
                }
            </div>
        }
    ], [recordBeingEdited, viewMode]);

    return (
        <div className="dataSourceTableContainer splitGrid">
            {formattedRecords.length > 0 &&
                <TPEReactTable {...{
                    data: formattedRecords,
                    columnDefinitions,
                    className: "dataSources",
                    sortable: false,
                    showHeader: false,
                }} />
            }
        </div>
    );
}
